/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   router.tsx
 *     Description     :   React Router v6 route configuration — ProtectedRoute guard, Shell layout, and all application routes.
 *     Module          :   005-web-shell
 *     Component Type  :   Router
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-07-27      Peter Dobler        Spec 031 (T073): the Universal per-migration
 *                                                     sub-route + its page chrome. No new top-level
 *                                                     nav; navRoutes.ts deliberately untouched.
 *     1.2         2026-07-27      Peter Dobler        Spec 031 (T092/T102/T103): the mapping
 *                                                     workbench, parameters and callability routes —
 *                                                     three finished screens that no module imported,
 *                                                     so the bundle carried no chunk for any of them.
 *                                                     navRoutes.ts now carries showInNav:false twins
 *                                                     per ui-screens.md §3.2
 *     1.3         2026-10-12      Peter Dobler        Spec 040 (T021): /schema-drift, the Schema
 *                                                     Drift page, beside DataMave Sync
 *     1.4         2026-10-25      Peter Dobler        Spec 044 (T032): /switches, the Switches page,
 *                                                     paired with its navRoutes.ts entry
 *     1.5         2026-10-29      Peter Dobler        Spec 046 (T024): /schema-management, the Schema
 *                                                     Management page, paired with its navRoutes.ts entry
 *     1.6         2026-10-30      Peter Dobler        Spec 046 (T097): /schema-drift redirects to the Schema
 *                                                     Management page's Comparison tab
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react react-router-dom lucide-react
 *     Internal        :   useAuth Shell pages navRoutes ui kit
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     AppRouter ProtectedRoute
 *
 * =========================================================================================
 */

import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ShieldQuestion, SlidersHorizontal, Table2, Warehouse } from "lucide-react";
import { useAuth } from "./auth/useAuth.js";
import { useCurrentUser } from "./auth/UserProvider.js";
import { Shell } from "./components/Shell.js";
import { Button, DetailLayout, ErrorState } from "./components/ui/index.js";

const SignIn = lazy(() => import("./pages/SignIn.js").then((m) => ({ default: m.SignIn })));
const Dashboard = lazy(() => import("./pages/Dashboard.js").then((m) => ({ default: m.Dashboard })));
const JobMonitor = lazy(() => import("./pages/JobMonitor.js").then((m) => ({ default: m.JobMonitor })));
const MigrationWizard = lazy(() => import("./pages/wizard/MigrationWizard.js").then((m) => ({ default: m.MigrationWizard })));
const MigrationControlCenter = lazy(() => import("./pages/console/MigrationControlCenter.js").then((m) => ({ default: m.MigrationControlCenter })));
const MigrationWorkflow = lazy(() => import("./pages/console/MigrationWorkflow.js").then((m) => ({ default: m.MigrationWorkflow })));
const Settings = lazy(() => import("./pages/Settings.js").then((m) => ({ default: m.Settings })));
const Users = lazy(() => import("./pages/Users.js").then((m) => ({ default: m.Users })));
const RemovePharmacySection = lazy(() =>
  import("./rp/RemovePharmacySection.js").then((m) => ({ default: m.RemovePharmacySection })),
);
const DataMaveSyncSection = lazy(() =>
  import("./pages/datamave/DataMaveSyncSection.js").then((m) => ({ default: m.DataMaveSyncSection })),
);
const SwitchesPage = lazy(() => import("./pages/Switches.js").then((m) => ({ default: m.SwitchesPage })));
const SchemaManagementPage = lazy(() =>
  import("./pages/schemaManagement/SchemaManagementPage.js").then((m) => ({ default: m.SchemaManagementPage })),
);
const NotFound = lazy(() => import("./pages/NotFound.js").then((m) => ({ default: m.NotFound })));
// Spec 031 (T072) — the Lakehouse presence check. A PANEL, not a page: it renders a `PageSection`
// (an <h2>) and no page chrome, so the route below supplies the single <h1>.
const UniversalSourcePanel = lazy(() =>
  import("./pages/console/universal/UniversalSourcePanel.js").then((m) => ({
    default: m.UniversalSourcePanel,
  })),
);
// Spec 031 (T092) — the mapping workbench. The ONLY new full screen: it carries its own page chrome,
// so unlike the three panels around it, it is mounted directly on its route.
const MapSourceTables = lazy(() =>
  import("./pages/console/universal/MapSourceTables.js").then((m) => ({
    default: m.MapSourceTables,
  })),
);
// Spec 031 (T102/T103) — two PANELS, like `UniversalSourcePanel`: each renders a `PageSection` (an
// <h2>) and no page chrome, so the routes below supply the single <h1> and the way back.
const UniversalParameters = lazy(() =>
  import("./pages/console/universal/UniversalParameters.js").then((m) => ({
    default: m.UniversalParameters,
  })),
);
const UniversalCallability = lazy(() =>
  import("./pages/console/universal/UniversalCallability.js").then((m) => ({
    default: m.UniversalCallability,
  })),
);

/** Holds while MSAL initializes, then redirects unauthenticated users to /signin. */
function ProtectedRoute(): React.JSX.Element {
  const { isAuthenticated, isInitializing } = useAuth();
  if (isInitializing) {
    // MSAL is still processing the redirect callback — never redirect here or the loop starts.
    return (
      <div className="flex items-center justify-center h-screen text-[var(--color-text-secondary)]">
        Signing in…
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  return <Outlet />;
}

/** Index route ("/"): once the user record loads, send them to their preferred starting page. */
function LandingRedirect(): React.JSX.Element {
  const { user, isLoading } = useCurrentUser();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-[var(--color-text-secondary)]">Loading…</div>
    );
  }
  return <Navigate to={user?.preferences.startingPage ?? "/dashboard"} replace />;
}

/** The four per-migration Universal screens, in the order an operator meets them. */
const UNIVERSAL_SCREENS = [
  { slug: "source", label: "Source", icon: Warehouse },
  { slug: "mapping", label: "Mapping", icon: Table2 },
  { slug: "parameters", label: "Parameters", icon: SlidersHorizontal },
  { slug: "callability", label: "Callability", icon: ShieldQuestion },
] as const;

/**
 * Cross-links between the four Universal screens, rendered in each one's header `actions`.
 *
 * 🔑 THIS IS THE AFFORDANCE, and without it two of these routes would be reachable only by typing a
 * URL — the same failure as having no route at all, one step further along. The console rail supplies
 * the two ENTRY points the design fixes (`Universal Source` on the action rail, and the `Mapping`
 * button on the `import-source` stage row, `ui-screens.md` §3.2); once inside the set, the operator
 * moves between the four here rather than going back out to the console each time.
 *
 * It NAVIGATES and nothing else — no control in this cluster starts, queues or schedules anything
 * (FR-029, SC-011).
 */
function UniversalScreenNav({
  migrationId,
  current,
}: {
  readonly migrationId: number;
  readonly current: (typeof UNIVERSAL_SCREENS)[number]["slug"];
}): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-2">
      {UNIVERSAL_SCREENS.filter((screen) => screen.slug !== current).map((screen) => (
        <Button
          key={screen.slug}
          variant="secondary"
          size="sm"
          leftIcon={<screen.icon className="h-4 w-4" />}
          onClick={() => navigate(`/migrations/${String(migrationId)}/universal/${screen.slug}`)}
          data-testid={`universal-nav-${screen.slug}`}
        >
          {screen.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Spec 031 (T073) — page chrome for the Universal source screen.
 *
 * `UniversalSourcePanel` is a PANEL: its outermost element is a `PageSection`, whose heading is an
 * `<h2>`. Mounting it straight onto a route would give a screen with no `<h1>` and no way back,
 * which the design blueprint's "one `<h1>` per page" rule forbids. `DetailLayout` is the kit's
 * per-entity shell and supplies both — it is also on the conformance guard's approved layout list,
 * so the composition is checked rather than merely conventional.
 *
 * **Back goes to the WORKFLOW, not to the console list.** The panel's home is the `copy-source`
 * stage row (`ui-screens.md` §2.2), and §3.2 fixes the same back-affordance for the Universal
 * screens, so the workflow is this screen's parent regardless of which button the operator arrived
 * on. The workflow's own breadcrumb then continues up to the Migration Control Center.
 */
function UniversalSourceScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const migrationId = id === undefined ? Number.NaN : Number.parseInt(id, 10);
  // A non-numeric `:id` still MATCHES this route. Guard it explicitly: the panel's queries are
  // `enabled: migrationId > 0`, so passing NaN through would render a spinner that never resolves —
  // a broken link presenting as a slow one.
  const valid = Number.isInteger(migrationId) && migrationId > 0;
  return (
    <DetailLayout
      title={valid ? `Universal Source — Migration #${String(migrationId)}` : "Universal Source"}
      subtitle="Whether this migration's files have landed in the Lakehouse, and the one act that records the answer."
      icon={<Warehouse className="h-6 w-6" />}
      backTo={valid ? `/migrations/${String(migrationId)}/workflow` : "/migrations"}
      backLabel={valid ? "Workflow" : "Migration Control Center"}
      actions={valid ? <UniversalScreenNav migrationId={migrationId} current="source" /> : undefined}
    >
      {valid ? (
        <UniversalSourcePanel migrationId={migrationId} />
      ) : (
        <ErrorState
          title="No migration in this address"
          message="This screen is per-migration. Open it from a migration in the Migration Control Center or from its workflow."
        />
      )}
    </DetailLayout>
  );
}

/**
 * Spec 031 (T102/T103) — page chrome for the two remaining Universal PANELS.
 *
 * Identical composition rule to {@link UniversalSourceScreen} and for the identical reason: both
 * components' outermost element is a `PageSection` (an `<h2>`), so mounting either straight onto a
 * route would produce a screen with no `<h1>` and no way back. `DetailLayout` supplies both and is on
 * the conformance guard's approved layout list. Back goes to the WORKFLOW, matching `ui-screens.md`
 * §3.2's back-affordance ruling for the Universal screens.
 *
 * The non-numeric `:id` guard is not optional. A route param like `/migrations/abc/universal/…` still
 * MATCHES, and every query in these panels is `enabled: migrationId > 0` — so passing NaN through
 * renders a spinner that never resolves: a broken link presenting as a slow one.
 */
function UniversalPanelScreen({
  slug,
  title,
  fallbackTitle,
  subtitle,
  icon,
  render,
}: {
  readonly slug: (typeof UNIVERSAL_SCREENS)[number]["slug"];
  readonly title: (migrationId: number) => string;
  readonly fallbackTitle: string;
  readonly subtitle: string;
  readonly icon: React.ReactNode;
  readonly render: (migrationId: number) => React.JSX.Element;
}): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const migrationId = id === undefined ? Number.NaN : Number.parseInt(id, 10);
  const valid = Number.isInteger(migrationId) && migrationId > 0;
  return (
    <DetailLayout
      title={valid ? title(migrationId) : fallbackTitle}
      subtitle={subtitle}
      icon={icon}
      backTo={valid ? `/migrations/${String(migrationId)}/workflow` : "/migrations"}
      backLabel={valid ? "Workflow" : "Migration Control Center"}
      actions={valid ? <UniversalScreenNav migrationId={migrationId} current={slug} /> : undefined}
    >
      {valid ? (
        render(migrationId)
      ) : (
        <ErrorState
          title="No migration in this address"
          message="This screen is per-migration. Open it from a migration in the Migration Control Center or from its workflow."
        />
      )}
    </DetailLayout>
  );
}

/** The top-level routing tree. */
export function AppRouter(): React.JSX.Element {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>}>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<Shell />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/migrations" element={<MigrationControlCenter />} />
              <Route path="/migrations/:id" element={<MigrationControlCenter />} />
              <Route path="/migrations/:id/workflow" element={<MigrationWorkflow />} />
              {/* Spec 031 (T073): the Universal per-migration screens. SUB-routes of an existing
                  per-migration path — no new top-level nav entry, by design.

                  🔴 `router/navRoutes.ts` IS INERT AS A RESOLVER, AND IS STILL NOT OPTIONAL. Its only
                  consumer is `NavSidebar`, which filters `showInNav !== false` before it renders
                  anything (`NavSidebar.tsx:69-71`), and its `element` field is never read by this
                  file — so a `showInNav: false` entry there puts no link in the sidebar and makes no
                  path resolve. THESE ROUTES are what make these paths resolve. But the pairing is
                  manual in BOTH directions and each direction fails differently: a nav entry without
                  a route is a sidebar link to a 404, and a route without a nav entry is a screen that
                  appears in no inventory of this application. `ui-screens.md` §3.2 requires the
                  mapping workbench in both files, and it now is. The three panel routes are not in
                  `navRoutes` for a typed reason — see the note on that entry. */}
              <Route path="/migrations/:id/universal/source" element={<UniversalSourceScreen />} />
              {/* Spec 031 (T092) — the mapping workbench, `ui-screens.md` §3.2. Registered LAST of the
                  four and the reason is worth stating: its page shipped complete, with tests, and was
                  imported by nothing — so the bundler emitted no chunk for it and the operator's
                  "actual mapping UI" was dead code. A screen is not shipped until a route reaches it.

                  It is mounted bare, without `DetailLayout`: unlike the three panels it IS a full
                  screen and carries its own chrome and its own breadcrumb to the workflow. */}
              <Route path="/migrations/:id/universal/mapping" element={<MapSourceTables />} />
              <Route
                path="/migrations/:id/universal/parameters"
                element={
                  <UniversalPanelScreen
                    slug="parameters"
                    title={(id) => `Universal Parameters — Migration #${String(id)}`}
                    fallbackTitle="Universal Parameters"
                    subtitle="The eight parameters of the legacy Set Universal Parameters form, stored on this migration's own parameter row."
                    icon={<SlidersHorizontal className="h-6 w-6" />}
                    render={(id) => <UniversalParameters migrationId={id} />}
                  />
                }
              />
              {/* 🔴 The verdict REPORTS. It never triggers (FR-029, SC-011): there is no control on
                  this route, or behind it, that starts, queues or schedules the conversion. */}
              <Route
                path="/migrations/:id/universal/callability"
                element={
                  <UniversalPanelScreen
                    slug="callability"
                    title={(id) => `Conversion Callability — Migration #${String(id)}`}
                    fallbackTitle="Conversion Callability"
                    subtitle="Whether the Universal Conversion is callable for this migration, precondition by precondition. This screen reports; it starts nothing."
                    icon={<ShieldQuestion className="h-6 w-6" />}
                    render={(id) => <UniversalCallability migrationId={id} />}
                  />
                }
              />
              <Route path="/wizard" element={<MigrationWizard />} />
              <Route path="/wizard/migrations/:id" element={<MigrationWizard />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/users" element={<Users />} />
              {/* Spec 016 (folded-in topology): the Remove Pharmacy section — admin-gated,
                  per-function screens nested under /remove-pharmacy/*. */}
              <Route path="/remove-pharmacy/*" element={<RemovePharmacySection />} />
              {/* Spec 028: the DataMave Sync admin section — Sync + Drift Report under /datamave-sync/*. */}
              <Route path="/datamave-sync/*" element={<DataMaveSyncSection />} />
              {/* Spec 046: the Schema Management page — the write catalog; any signed-in user reads it. */}
              <Route path="/schema-management" element={<SchemaManagementPage />} />
              {/* Spec 040's comparison is Schema Management's Comparison tab since spec 046 T097 (FR-022). */}
              <Route path="/schema-drift" element={<Navigate to="/schema-management?tab=comparison" replace />} />
              {/* Spec 044: the feature switches — any signed-in user reads them; an Administrator changes one. */}
              <Route path="/switches" element={<SwitchesPage />} />
              <Route path="/jobs/:id" element={<JobMonitor />} />
            </Route>
          </Route>
          <Route path="/" element={<LandingRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
